import React, { useEffect, useMemo, useState } from "react"
import { Container, Row, Col, Card, CardBody, Badge, Button, Input, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { useDispatch, useSelector } from "react-redux"
import { fetchProviderInvoicesRequest } from "../../store/invoices/actions"
import { jsPDF } from "jspdf"
import "jspdf-autotable"
import autoTable from "jspdf-autotable"
import html2canvas from "html2canvas"

const NODE_API = "https://node-server-eta-coral.vercel.app"

const InvoicesPage = () => {
  // meta title
  document.title = "الفواتير | DBsaloni SP Admin & Dashboard"


  const dispatch = useDispatch()
  const { items, loading, error } = useSelector(state => state.invoices || { items: [], loading: false, error: null })


  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [erpInvoice, setErpInvoice] = useState(null)
  const [fetchingERP, setFetchingERP] = useState(false)
  const [erpError, setErpError] = useState(null)
  const [qrBlobUrl, setQrBlobUrl] = useState(null)


  useEffect(() => {
    dispatch(fetchProviderInvoicesRequest())
  }, [dispatch])


  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter(x =>
      (x.erp_invoice_id || "").toLowerCase().includes(q) ||
      (x.service_name || "").toLowerCase().includes(q)
    )
  }, [items, query])


  const openModal = (inv) => {
    setSelected(inv)
    setModalOpen(true)
  }


  useEffect(() => {
    if (!modalOpen || !selected?.erp_invoice_id) return
    const controller = new AbortController()
    const loadERP = async () => {
      try {
        setFetchingERP(true)
        setErpError(null)
        setErpInvoice(null)
        setQrBlobUrl(null)


        const res = await fetch(`${NODE_API}/invoice/${encodeURIComponent(selected.erp_invoice_id)}`, {
          headers: { "Content-Type": "application/json" },
          signal: controller.signal
        })
        const json = await res.json()
        const inv = json?.data || json
        if (!inv?.name) {
          setErpError("لم يتم العثور على الفاتورة في ERP.")
          return
        }
        setErpInvoice(inv)


        if (inv.ksa_einv_qr) {
          try {
            const imgRes = await fetch(`${NODE_API}/erp-image?path=${encodeURIComponent(inv.ksa_einv_qr)}`, { signal: controller.signal })
            if (imgRes.ok) {
              const blob = await imgRes.blob()
              setQrBlobUrl(URL.createObjectURL(blob))
            }
          } catch {
            // ignore QR fetch failure
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") setErpError(e.message || "فشل جلب الفاتورة")
      } finally {
        setFetchingERP(false)
      }
    }
    loadERP()
    return () => controller.abort()
  }, [modalOpen, selected?.erp_invoice_id])


  useEffect(() => {
    return () => {
      if (qrBlobUrl) URL.revokeObjectURL(qrBlobUrl)
    }
  }, [qrBlobUrl])


  const downloadInvoicePDF = async (invoice, qrBlobUrl) => {
    if (!invoice) return;
    // Settlement breakdown
    const gross = Number(invoice.gross_amount || invoice.grand_total || 0);
    const rate = Number(invoice.commission_rate || 0);
    const commission = Number(invoice.commission_amount ?? (gross * rate) / 100);
    const taxable = Number(invoice.salon_net_before_vat ?? gross - commission);
    const vat = Number(invoice.salon_vat ?? taxable * 0.15);
    const cash = Number(invoice.settlement_amount ?? taxable - vat);

    // Create a temporary div with Arabic content
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '800px';
    tempDiv.style.padding = '40px';
    tempDiv.style.backgroundColor = 'white';
    tempDiv.style.fontFamily = 'Arial, sans-serif';
    
    tempDiv.innerHTML = `
      <div style="text-align: center; margin-bottom: 30px;">
        <h1 style="color: #2980b9; margin: 0;">DBSaloni</h1>
        <h3 style="color: #555; margin: 10px 0;">فاتورة ضريبية / Tax Invoice</h3>
        <hr style="border: 2px solid #2980b9;">
      </div>
      
      <div style="display: flex; justify-content: space-between; margin-bottom: 30px;">
        <div style="text-align: right;">
          <p><strong>رقم الفاتورة:</strong> ${invoice.name || 'N/A'}</p>
          <p><strong>اسم العميل:</strong> ${invoice.customer_name || invoice.customer || 'N/A'}</p>
          <p><strong>الشركة:</strong> ${invoice.company || 'DB Saloni'}</p>
          <p><strong>التاريخ:</strong> ${invoice.posting_date || 'N/A'}</p>
        </div>
        ${qrBlobUrl ? `<div><img src="${qrBlobUrl}" style="width: 120px; height: 120px;"/></div>` : ''}
      </div>
      
      <div style="background: #27ae60; color: white; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
        <h2 style="margin: 0;">المجموع / Total: ${invoice.grand_total ?? 0} ${invoice.currency || 'SAR'}</h2>
      </div>

      <div style="margin-bottom: 25px;">
        <h4 style="margin-bottom: 10px;">تفصيل المستحق</h4>
        <table style="width: 100%; border-collapse: collapse;">
          <tbody>
            <tr><th style="text-align:right; padding:8px; border:1px solid #ddd;">المبلغ الخاضع للضريبة</th><td style="text-align:right; padding:8px; border:1px solid #ddd;">${taxable.toFixed(2)} ر.س</td></tr>
            <tr><th style="text-align:right; padding:8px; border:1px solid #ddd;">ضريبة القيمة المضافة 15٪</th><td style="text-align:right; padding:8px; border:1px solid #ddd;">${vat.toFixed(2)} ر.س</td></tr>
            <tr><th style="text-align:right; padding:8px; border:1px solid #ddd;">المبلغ المستلم نقداً</th><td style="text-align:right; padding:8px; border:1px solid #ddd; color:green; font-weight:bold;">${cash.toFixed(2)} ر.س</td></tr>
            <tr><th style="text-align:right; padding:8px; border:1px solid #ddd;">الضريبة المستحقة</th><td style="text-align:right; padding:8px; border:1px solid #ddd; color:#d69d00; font-weight:bold;">على الشركة</td></tr>
            <tr><th style="text-align:right; padding:8px; border:1px solid #ddd;">صافي دخل الصالون</th><td style="text-align:right; padding:8px; border:1px solid #ddd; font-weight:bold;">${cash.toFixed(2)} ر.س</td></tr>
          </tbody>
        </table>
      </div>
      
      ${Array.isArray(invoice.items) && invoice.items.length > 0 ? `
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
          <thead>
            <tr style="background: #2980b9; color: white;">
              <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">الصنف / Item</th>
              <th style="padding: 10px; text-align: center; border: 1px solid #ddd;">الكمية / Qty</th>
              <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">السعر / Price</th>
              <th style="padding: 10px; text-align: right; border: 1px solid #ddd;">المجموع / Total</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items.map(item => `
              <tr>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${item.item_name || item.description || item.item_code || '—'}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.qty ?? 1}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${(item.rate ?? 0).toFixed(2)}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${((item.qty ?? 1) * (item.rate ?? 0)).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : ''}
      
      <hr style="border: 1px solid #ddd; margin: 30px 0;">
      <div style="text-align: center; color: #666;">
        <p style="margin: 5px 0;">شكراً لاستخدامكم DBSaloni - Thank you for using DBSaloni</p>
        <p style="margin: 5px 0;">www.dbsaloni.com | support@dbsaloni.com</p>
      </div>
    `;
    
    document.body.appendChild(tempDiv);
    
    try {
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`DBSaloni_Invoice_${invoice.name || Date.now()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('حدث خطأ أثناء إنشاء الفاتورة');
    } finally {
      document.body.removeChild(tempDiv);
    }
  }


  const addBreakdown = (inv) => {
    const gross = Number(inv.gross_amount || inv.total_after_discount || inv.total || 0);
    const rate = Number(inv.commission_rate || inv.commission_rates || 0);
    const commission = Number(inv.commission_amount ?? (gross * rate) / 100);
    const taxable = Number(inv.salon_net_before_vat ?? gross - commission);
    const vat = Number(inv.salon_vat ?? taxable * 0.15);
    const settlement = Number(inv.settlement_amount ?? taxable - vat);
    return { gross, rate, commission, taxable, vat, settlement };
  };


  return (
    <div className="page-content">
      <Container fluid={false}>
        <Breadcrumbs
          title=" الفواتير"
          breadcrumbItem="ادارة الفواتير"
        />


        <div className="d-flex align-items-center mb-3">
          <Input
            type="search"
            placeholder="ابحث برقم الفاتورة أو اسم الخدمة..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="me-2"
          />
          <Badge color="info">الإجمالي: {filtered.length}</Badge>
        </div>


        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">جاري تحميل فواتيرك...</p>
          </div>
        ) : error ? (
          <Alert color="danger">حدث خطأ: {error}</Alert>
        ) : filtered.length === 0 ? (
          <Alert color="info">لا توجد فواتير لعرضها.</Alert>
        ) : (
          <Row className="g-3">
            {filtered.map(inv => {
              const breakdown = addBreakdown(inv);
              return (
                <Col xl="4" lg="6" key={inv.id}>
                  <Card className="shadow-sm h-100">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">{inv.service_name || "خدمة"}</h5>
                        <Badge color="success">{inv.status || "مكتمل"}</Badge>
                      </div>
                      <p className="text-muted mb-2">
                        رقم فاتورة ERP: <strong>{inv.erp_invoice_id}</strong>
                      </p>
                      <div className="d-flex justify-content-between">
                        <div>
                          <small className="text-muted">الإجمالي (قبل العمولة)</small>
                          <div className="fw-bold">{breakdown.gross.toFixed(2)} ر.س</div>
                        </div>
                        <div>
                          <small className="text-muted">عمولة المنصة</small>
                          <div className="fw-bold text-danger">{breakdown.commission.toFixed(2)} ر.س</div>
                        </div>
                        <div>
                          <small className="text-muted">الصافي (بعد الضريبة)</small>
                          <div className="fw-bold text-success">{breakdown.settlement.toFixed(2)} ر.س</div>
                        </div>
                      </div>
                      <div className="mt-2 text-muted">
                        <small>Taxable: {breakdown.taxable.toFixed(2)} | VAT 15%: {breakdown.vat.toFixed(2)}</small>
                      </div>
                      <div className="mt-3 d-flex justify-content-between">
                        <Button color="primary" size="sm" onClick={() => openModal(inv)}>
                          عرض فاتورة ERP
                        </Button>
                        <small className="text-muted">
                          {new Date(inv.posting_date || inv.created_at || Date.now()).toLocaleDateString("ar-SA")}
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}


        <ERPInvoiceModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(!modalOpen)}
          invoice={erpInvoice}
          selected={selected}
          erpError={erpError}
          loading={fetchingERP}
          qrBlobUrl={qrBlobUrl}
          downloadInvoicePDF={downloadInvoicePDF}
        />
      </Container>
    </div>
  )
}


const ERPInvoiceModal = ({ isOpen, toggle, invoice, selected, erpError, loading, qrBlobUrl, downloadInvoicePDF }) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>تفاصيل فاتورة ERP</ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">جاري تحميل الفاتورة...</p>
          </div>
        ) : erpError ? (
          <Alert color="danger">{erpError}</Alert>
        ) : invoice ? (
          <>
            {/* From / To Header with platform details */}
            <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
              <CardBody>
                <Row className="align-items-start">
                  <Col md={6} className="text-end">
                    <h6 className="mb-2"><strong>من - الجهة المُصدرة:</strong></h6>
                    <p className="mb-1"><strong>الاسم:</strong> {selected?.platform?.company_name || selected?.platform?.name || 'DB Saloni'}</p>
                    <p className="mb-1"><strong>السجل التجاري:</strong> {selected?.platform?.commercial_register || selected?.platform?.commercialRegister || 'غير متوفر'}</p>
                    <p className="mb-1"><strong>رقم الضريبة:</strong> {selected?.platform?.vat_number || selected?.platform?.vatNumber || 'غير متوفر'}</p>
                    <p className="mb-1"><strong>البلد:</strong> {selected?.platform?.country || 'غير متوفر'}</p>
                    <p className="mb-0"><strong>العنوان:</strong> {selected?.platform?.address || 'غير متوفر'}</p>
                  </Col>

                  <Col md={1} className="text-center d-flex align-items-center justify-content-center">
                    <span style={{ fontSize: '20px' }}>←</span>
                  </Col>

                  <Col md={5}>
                    <h6 className="mb-2"><strong>إلى - العميل:</strong></h6>
                    <p className="mb-1"><strong>الاسم:</strong> {invoice?.customer_name || invoice?.customer || 'غير متوفر'}</p>
                    <p className="mb-1"><strong>الشركة:</strong> {invoice?.company || 'غير متوفر'}</p>
                    <p className="mb-0"><strong>رقم الفاتورة:</strong> {invoice?.name || 'غير متوفر'}</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardBody>
                <Row>
                  <Col md={6}>
                    <p><strong>رقم الفاتورة:</strong> {invoice.name || "N/A"}</p>
                    <p><strong>اسم العميل:</strong> {invoice.customer_name || invoice.customer || "N/A"}</p>
                    <p><strong>الشركة:</strong> {invoice.company || "N/A"}</p>
                  </Col>
                  <Col md={6} className="text-end">
                    <p><strong>التاريخ:</strong> {invoice.posting_date || "N/A"}</p>
                    <p><strong>المجموع:</strong> {invoice.grand_total ?? 0} {invoice.currency || "ر.س"}</p>
                    {qrBlobUrl ? (
                      <img
                        src={qrBlobUrl}
                        alt="ZATCA QR"
                        style={{ width: 128, height: 128, objectFit: "contain" }}
                      />
                    ) : (
                      <small className="text-muted">لا يوجد رمز QR</small>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>


            {/* Settlement breakdown */}
            <Card className="mb-3 bg-light">
              <CardBody>
                <h6 className="mb-3">تفصيل المستحق</h6>
                {(() => {
                  const gross = Number(invoice.gross_amount || invoice.grand_total || 0);
                  const rate = Number(invoice.commission_rate || 0);
                  const commission = Number(invoice.commission_amount ?? (gross * rate) / 100);
                  const taxable = Number(invoice.salon_net_before_vat ?? gross - commission);
                  const vat = Number(invoice.salon_vat ?? taxable * 0.15);
                  const cash = Number(invoice.settlement_amount ?? taxable - vat);
                  return (
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0">
                        <tbody>
                          <tr><th>المبلغ الخاضع للضريبة</th><td>{taxable.toFixed(2)} ر.س</td></tr>
                          <tr><th>ضريبة القيمة المضافة 15٪</th><td>{vat.toFixed(2)} ر.س</td></tr>
                          <tr><th>المبلغ المستلم نقداً</th><td className="text-success fw-bold">{cash.toFixed(2)} ر.س</td></tr>
                          <tr><th>الضريبة المستحقة</th><td><span className="text-warning fw-semibold">على الشركة</span></td></tr>
                          <tr><th>صافي دخل الصالون</th><td className="fw-bold">{cash.toFixed(2)} ر.س</td></tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </CardBody>
            </Card>

            {Array.isArray(invoice.items) && invoice.items.length > 0 && (
              <Card>
                <CardBody>
                  <h6 className="mb-3">المنتجات/الخدمات</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>الصنف</th>
                          <th>الكمية</th>
                          <th className="text-end">السعر</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.items.map((it, idx) => (
                          <tr key={idx}>
                            <td>{it.item_name || it.description || it.item_code || "—"}</td>
                            <td>{it.qty ?? 0}</td>
                            <td className="text-end">{it.rate ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            )}
          </>
        ) : (
          <Alert color="info">لا توجد بيانات للعرض.</Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button 
          color="success" 
          onClick={() => downloadInvoicePDF(invoice, qrBlobUrl)}
          disabled={!invoice}
        >
          <i className="mdi mdi-download me-1" />
          تحميل PDF
        </Button>
        <Button color="secondary" onClick={toggle}>إغلاق</Button>
      </ModalFooter>
    </Modal>
  )
}

export default InvoicesPage